import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "../api/api";
import { Bell, X } from "lucide-react";

const NotificacoesDropdown = ({ usuarioId }) => {
  const [notificacoes, setNotificacoes] = useState([]);
  const [aberto, setAberto] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const dropdownRef = useRef(null);
  const dropdownWidth = "min(360px, calc(100vw - 24px))";

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const getStatusFromNotification = (n) => {
    const text = `${n.titulo || ""} ${n.mensagem || ""}`.toLowerCase();
    if (text.includes("pendente")) return "pendente";
    if (text.includes("confirmado")) return "confirmado";
    if (text.includes("cancelado")) return "cancelado";
    if (text.includes("finalizado")) return "finalizado";
    return null;
  };

  const statusColors = {
    pendente: { bg: "#fef3c7", fg: "#d97706", shadow: "#f59e0b33" },
    confirmado: { bg: "#dcfce7", fg: "#16a34a", shadow: "#22c55e33" },
    cancelado: { bg: "#fee2e2", fg: "#dc2626", shadow: "#ef444433" },
    finalizado: { bg: "#f1f5f9", fg: "#64748b", shadow: "#64748b33" },
  };

  const formatMensagemPendente = (mensagem) => {
    const texto = String(mensagem || "").trim();
    const match = texto.match(/^Novo agendamento de\s+(.+?)\s+para\s+(.+?)\s+em\s+(.+?)\s*\((.+?)\)\.?$/i);

    if (!match) return texto;

    const cliente = (match[1] || "").trim();
    const servico = (match[2] || "").trim();
    const dataHoraRaw = (match[4] || "").trim();
    const dataHora = dataHoraRaw.replace(/\s*-\s*às\s*/i, " às ");

    return `Novo agendamento recebido.\n\nCliente: ${cliente}\nServiço: ${servico}\n${dataHora}`;
  };

  const carregarNotificacoes = useCallback(async () => {
    if (!usuarioId) return;
    try {
      const res = await api.get(`/notificacoes/usuario/${usuarioId}`);
      setNotificacoes(res.data);
    } catch (err) {
      console.error('Erro ao buscar notificações:', err);
      setNotificacoes([]);
    }
  }, [usuarioId]);

  useEffect(() => {
    carregarNotificacoes();
  }, [carregarNotificacoes]);

  useEffect(() => {
    const handler = () => carregarNotificacoes();
    window.addEventListener("fz:notificacao", handler);
    return () => window.removeEventListener("fz:notificacao", handler);
  }, [carregarNotificacoes]);

  useEffect(() => {
    if (!aberto) return;
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setAberto(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [aberto]);

  const novas = notificacoes.filter(n => !n.lida).length;

  const marcarComoLida = async (id) => {
    await api.patch(`/notificacoes/${id}/lida`);
    setNotificacoes(nots => nots.map(n => n.id === id ? { ...n, lida: true } : n));
  };

  return (
    <div ref={dropdownRef} style={{ position: "relative", display: "inline-block" }}>
      <button
        onClick={() => setAberto(a => !a)}
        style={{ background: "none", border: "none", position: "relative", cursor: "pointer", padding: 0 }}
      >
        <Bell size={24} color={novas > 0 ? "#2563eb" : "#888"} />
        {novas > 0 && (
          <span style={{ position: "absolute", top: -6, right: -6, background: "#ef4444", color: "#fff", borderRadius: "50%", fontSize: 12, fontWeight: 700, padding: "2px 7px", boxShadow: "0 2px 8px #ef444455" }}>{novas}</span>
        )}
      </button>
      {aberto && (
        <div style={{ position: isMobile ? "fixed" : "absolute", right: 8, left: isMobile ? 8 : "auto", top: isMobile ? 66 : 36, width: isMobile ? "auto" : dropdownWidth, maxWidth: isMobile ? "none" : "calc(100vw - 24px)", background: "var(--card)", color: "var(--text)", boxShadow: "0 8px 32px rgba(37,99,235,0.10)", borderRadius: 14, zIndex: 120, padding: 16, border: "1px solid var(--line)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <h4 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: "#2563eb", letterSpacing: 0.2 }}>Notificações</h4>
            {isMobile && (
              <button
                onClick={() => setAberto(false)}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "var(--muted)",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                aria-label="Fechar notificações"
              >
                <X size={18} />
              </button>
            )}
          </div>
          <div style={{ maxHeight: "min(60vh, 340px)", overflowY: "auto", marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
            {notificacoes.length === 0 ? (
              <p className="muted" style={{ margin: 0, color: "var(--muted)", fontSize: 15 }}>Nenhuma notificação.</p>
            ) : (
              notificacoes.map(n => {
                const isLembrete = n.tipo === 'lembrete';
                const status = getStatusFromNotification(n);
                const mensagemFormatada = status === "pendente"
                  ? formatMensagemPendente(n.mensagem)
                  : n.mensagem;
                const statusColor = status ? statusColors[status] : null;
                const baseBg = statusColor ? statusColor.bg : (n.lida ? "#f9fafb" : "#e0e7ff");
                const baseFg = statusColor ? statusColor.fg : (n.lida ? "#888" : "#2563eb");
                const baseShadow = statusColor ? statusColor.shadow : (isLembrete ? "#ea580c33" : "#2563eb22");
                return (
                  <div key={n.id} style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 14,
                    background: isLembrete ? (n.lida ? "#fff7ed" : "#ffedd5") : baseBg,
                    borderRadius: 10,
                    boxShadow: n.lida ? "none" : (isLembrete ? "0 2px 8px #ea580c33" : `0 2px 8px ${baseShadow}`),
                    padding: "14px 16px",
                    border: "1px solid #e5e7eb"
                  }}>
                    <Bell size={22} color={isLembrete ? "#ea580c" : baseFg} style={{ marginTop: 2 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 15, color: isLembrete ? "#ea580c" : baseFg, marginBottom: 2, overflowWrap: "anywhere" }}>{n.titulo}</div>
                      <div style={{ fontSize: 13.5, color: "var(--text)", marginBottom: 8, whiteSpace: "pre-line", overflowWrap: "anywhere" }}>{mensagemFormatada}</div>
                      <button
                        style={{
                          background: n.lida ? "#e5e7eb" : (isLembrete ? "#ea580c" : baseFg),
                          color: n.lida ? "#888" : "#fff",
                          border: "none",
                          borderRadius: 7,
                          fontSize: 13,
                          fontWeight: 600,
                          padding: "5px 16px",
                          cursor: n.lida ? "default" : "pointer",
                          boxShadow: n.lida ? "none" : (isLembrete ? "0 2px 8px #ea580c33" : `0 2px 8px ${baseShadow}`),
                          transition: "background 0.2s",
                          maxWidth: "100%"
                        }}
                        onClick={() => marcarComoLida(n.id)}
                        disabled={n.lida}
                      >{n.lida ? "Lida" : "Marcar como lida"}</button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificacoesDropdown;
